import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getStudentBookingsPage, getStudentChildrenFull } from "../../../services/dataService";
import { SESSION_FILTERS, sessionsZoneNote, sortChildren } from "../../../utils/studentDetail";
import { showingLabel } from "../../../utils/kitPresentation";
import { TableCard } from "../../../components/kit/SectionCard";
import Pager from "../../../components/kit/Pager";
import ToolbarMenu from "../../../components/kit/ToolbarMenu";
import { PAGE_SIZE } from "./TabState";
import { usePagedQuery } from "./usePagedQuery";
import { useUsersByIds } from "./useUsersByIds";
import SessionsTable from "./SessionsTable";

// Every lesson this account booked, newest first (044 D35). The five status chips are one
// "Status: …" menu now (047 D16) with the same choices and the same queries: "All" on
// (studentId, startTimeUTC desc), a status on (studentId, status, startTimeUTC desc).
// Upcoming and Awaiting outcome bound Pending at the moment the choice was made, so every
// page of one choice splits at the same instant (D38). Child names come from the
// subcollection once (002 D5).
const SessionsTab = () => {
  const { student } = useOutletContext();
  const uid = student.id;
  const [filter, setFilter] = useState("all");
  const [now, setNow] = useState(() => new Date());
  const [children, setChildren] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getStudentChildrenFull(uid)
      .then((rows) => !cancelled && setChildren(sortChildren(rows)))
      .catch((e) => console.error("Error loading children:", e));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const current = SESSION_FILTERS.find((f) => f.key === filter);
  const { status, when } = current;
  const fetchPage = useCallback(
    (cursor) => getStudentBookingsPage(uid, { status, when, now, pageSize: PAGE_SIZE, cursor }),
    [uid, status, when, now]
  );
  const sessions = usePagedQuery(fetchPage, filter, "Failed to load the sessions.");
  const usersById = useUsersByIds(sessions.rows.map((b) => b.teacherId));
  const childrenById = Object.fromEntries(children.map((c) => [c.id, c]));

  // ToolbarMenu calls this only for a new choice, so picking the current one keeps the page.
  const choose = (key) => {
    setFilter(key);
    setNow(new Date());
  };

  return (
    <TableCard
      title="Sessions"
      subtitle={`Newest first · open a row to act on the lesson · ${sessionsZoneNote(sessions.rows, { timeZone: student.timeZone })}`}
      toolbar={<ToolbarMenu id="sessions-status" label="Status" value={filter} options={SESSION_FILTERS} onChange={choose} />}
      footer={
        <Pager
          label={sessions.loading || sessions.error ? "" : showingLabel(sessions.page, sessions.rows.length, PAGE_SIZE)}
          page={sessions.page}
          hasMore={sessions.hasMore}
          onPage={sessions.load}
        />
      }
    >
      <SessionsTable
        rows={sessions.rows}
        childrenById={childrenById}
        usersById={usersById}
        holder={student}
        timeZone={student.timeZone}
        loading={sessions.loading}
        error={sessions.error}
        onRetry={() => sessions.load(sessions.page)}
        emptyText={current.empty}
      />
    </TableCard>
  );
};

export default SessionsTab;
